import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Edit, Trash2, Clock, Flame, Users, ChefHat, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import RecipeImage from '../components/RecipeImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RecipeDetailPage() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { recipeId } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerateImage = async () => {
    setRegenerating(true);
    try {
      const res = await fetch(`${API}/recipes/${recipeId}/regenerate-image`, {
        method: 'POST', credentials: 'include',
      });
      const d = await res.json();
      if (res.ok) {
        setRecipe(d);
        toast.success(language === 'es' ? 'Imagen regenerada con IA' : 'Image regenerated with AI');
      } else {
        toast.error(d.detail || t('errors.generic'));
      }
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => {
    fetchRecipe();
  }, [recipeId]);

  const fetchRecipe = async () => {
    try {
      const response = await fetch(`${API}/recipes/${recipeId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRecipe(data);
      } else {
        toast.error(t('errors.notFound'));
        navigate('/recipes');
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API}/recipes/${recipeId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(t('success.deleted'));
        navigate('/recipes');
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error(t('errors.generic'));
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!recipe) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-stone">{t('errors.notFound')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Hero Image */}
        <div className="relative h-64 md:h-80 w-full overflow-hidden">
          <RecipeImage
            src={recipe.image}
            alt={recipe.name}
            containerClassName="w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent"></div>

          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/recipes')}
            className="absolute top-4 left-4 bg-white/90 hover:bg-white text-charcoal rounded-full z-20"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t('back')}
          </Button>

          {/* Category Badges */}
          <div className="absolute top-4 right-4 z-20 flex flex-wrap gap-2 justify-end max-w-[60%]">
            {recipe.categories && recipe.categories.length > 0 ? (
              recipe.categories.map(cat => (
                <span key={cat} className="bg-white/90 backdrop-blur-sm text-charcoal font-manrope font-semibold text-[10px] md:text-xs uppercase tracking-wide px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm">
                  {t(`recipes.categories.${cat}`)}
                </span>
              ))
            ) : (
              <span className="bg-white/90 backdrop-blur-sm text-charcoal font-manrope font-semibold text-xs uppercase tracking-wide px-3 py-1.5 rounded-full shadow-sm">
                {t(`recipes.categories.${recipe.category}`)}
              </span>
            )}
          </div>

          {/* Regenerate the AI image (always available for Premium) */}
          {user?.plan === 'premium' && (
            <Button
              onClick={handleRegenerateImage}
              disabled={regenerating}
              className="absolute bottom-4 right-4 z-20 bg-white/90 hover:bg-white text-charcoal rounded-full shadow-sm"
              size="sm"
              title={language === 'es' ? 'Generar una imagen nueva con IA' : 'Generate a new image with AI'}
              data-testid="regenerate-image-button"
            >
              {regenerating
                ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                : <Sparkles className="w-4 h-4 sm:mr-2" strokeWidth={1.5} />}
              <span className="hidden sm:inline">
                {regenerating
                  ? (language === 'es' ? 'Generando...' : 'Generating...')
                  : (language === 'es' ? 'Regenerar imagen' : 'Regenerate image')}
              </span>
            </Button>
          )}

          {/* Title Overlay: grows to fit the full title (no inner scrollbar, no clamping) */}
          <div className="absolute bottom-0 left-0 right-0 p-6 z-10 bg-gradient-to-t from-charcoal/95 via-charcoal/60 to-transparent pt-16">
            <h1 className="font-manrope font-bold text-2xl md:text-4xl text-white mb-2 break-words">
              {recipe.name}
            </h1>
            {recipe.description && (
              <p className="font-inter text-white/80 text-base md:text-lg max-w-2xl line-clamp-2 md:line-clamp-3">
                {recipe.description}
              </p>
            )}
          </div>
        </div>

        <div className="p-6 md:p-8">
          {/* Quick Info */}
          <div className="flex flex-wrap gap-4 mb-8">
            {recipe.prep_time && (
              <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full">
                <Clock className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <span className="font-inter text-charcoal">{recipe.prep_time} min</span>
              </div>
            )}
            {recipe.servings != null && (
              <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full">
                <Users className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <span className="font-inter text-charcoal">
                  {recipe.servings} {language === 'es' ? (recipe.servings === 1 ? 'ración' : 'raciones') : (recipe.servings === 1 ? 'serving' : 'servings')}
                </span>
              </div>
            )}
            {recipe.calories && (
              <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full">
                <Flame className="w-5 h-5 text-sage" strokeWidth={1.5} />
                <span className="font-inter text-charcoal">
                  {recipe.calories} kcal<span className="text-stone text-sm">/{language === 'es' ? 'ración' : 'serving'}</span>
                  {recipe.calories_approximate && (
                    <span className="text-stone text-sm ml-1">(aprox.)</span>
                  )}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full">
              <Users className="w-5 h-5 text-stone" strokeWidth={1.5} />
              <span className="font-inter text-charcoal">
                {language === 'es' ? 'Máx.' : 'Max'} {recipe.max_repetitions}x/{language === 'es' ? 'semana' : 'week'}
              </span>
            </div>
          </div>

          {/* Macros */}
          {(recipe.protein_g != null || recipe.carbs_g != null || recipe.fat_g != null || recipe.fiber_g != null) && (
            <div className="mb-8">
              <p className="font-manrope font-semibold text-charcoal mb-2">
                {t('recipes.nutrition.title')}
                <span className="text-stone text-sm ml-1">({language === 'es' ? 'por ración' : 'per serving'})</span>
                {recipe.macros_approximate && (
                  <span className="text-stone text-sm ml-1">({language === 'es' ? 'aprox.' : 'approx.'})</span>
                )}
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { v: recipe.protein_g, label: t('recipes.nutrition.protein') },
                  { v: recipe.carbs_g, label: t('recipes.nutrition.carbs') },
                  { v: recipe.fat_g, label: t('recipes.nutrition.fat') },
                  { v: recipe.fiber_g, label: t('recipes.nutrition.fiber') },
                ].filter(m => m.v != null).map((m, i) => (
                  <div key={i} className="bg-muted/50 px-4 py-2 rounded-full">
                    <span className="font-inter text-sm text-stone">{m.label}: </span>
                    <span className="font-inter font-semibold text-charcoal">{m.v} g</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Ingredients */}
            <div className="lg:col-span-1">
              <Card className="bg-white rounded-3xl shadow-soft p-6">
                <h2 className="font-manrope font-semibold text-xl text-charcoal mb-4 flex items-center gap-2">
                  <ChefHat className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  {t('recipes.form.ingredients')}
                </h2>

                {recipe.ingredients && recipe.ingredients.length > 0 ? (
                  <ul className="space-y-3">
                    {recipe.ingredients.map((ing, index) => (
                      <li key={index} className="flex justify-between items-start py-3 border-b border-border/50 last:border-0 gap-4">
                        <span className="font-inter text-charcoal flex-1 leading-tight">
                          {ing.name.charAt(0).toUpperCase() + ing.name.slice(1)}
                        </span>
                        <span className="font-inter text-stone text-sm text-right leading-tight max-w-[60%]">{ing.quantity}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="font-inter text-stone italic">
                    {language === 'es' ? 'Sin ingredientes especificados' : 'No ingredients specified'}
                  </p>
                )}
              </Card>
            </div>

            {/* Preparation Steps */}
            <div className="lg:col-span-2">
              <Card className="bg-white rounded-3xl shadow-soft p-6">
                <h2 className="font-manrope font-semibold text-xl text-charcoal mb-6">
                  {language === 'es' ? 'Preparación' : 'Preparation'}
                </h2>

                {recipe.preparation && recipe.preparation.length > 0 ? (
                  <ol className="space-y-6">
                    {recipe.preparation.map((step, index) => (
                      <li key={index} className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="font-manrope font-bold text-primary">{step.step_number || index + 1}</span>
                        </div>
                        <div className="flex-1 pt-2">
                          <p className="font-inter text-charcoal leading-relaxed">
                            {step.instruction}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="font-inter text-stone italic">
                    {language === 'es'
                      ? 'No hay pasos de preparación. Añádelos editando la receta.'
                      : 'No preparation steps. Add them by editing the recipe.'}
                  </p>
                )}
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-8">
            <Button
              onClick={() => navigate(`/recipes/${recipeId}/edit`)}
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 font-manrope font-bold"
              data-testid="edit-recipe-button"
            >
              <Edit className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('edit')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              className="rounded-full py-6 text-destructive hover:text-destructive border-destructive/30 hover:border-destructive"
              data-testid="delete-recipe-button"
            >
              <Trash2 className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-manrope text-charcoal">
                {t('delete')} "{recipe.name}"
              </DialogTitle>
            </DialogHeader>
            <p className="font-inter text-stone py-4">
              {t('recipes.deleteConfirm')}
            </p>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="rounded-full"
              >
                {t('cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="rounded-full"
                data-testid="confirm-delete-button"
              >
                {t('delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
